/**
 * Картинка с фолбэком-заглушкой при ошибке загрузки.
 *
 * Показывает серый квадрат с иконкой "картинка не загрузилась" если:
 *   - URL недоступен (404, 403, таймаут)
 *   - ответ не картинка
 *   - CORS запретил загрузку
 *
 * По умолчанию ставим `loading="lazy"` — браузер начинает качать картинку
 * только когда она близка к viewport. Это стандартный дефолт для Chrome/Safari
 * с 2023 года, используется в Next.js Image, Cloudinary и других крупных
 * либах. Если нужна немедленная загрузка (hero-картинка над складкой,
 * LCP-элемент) — явно передавай `loading="eager"`.
 *
 * Раньше этот компонент жил в app/components/figma/ — это было название
 * от Figma Make (инструмента экспорта макетов). К Figma компонент никак
 * не привязан — чистый React, поэтому перенесли в shared/ui/.
 */
import { useState, type ImgHTMLAttributes } from 'react';

const ERROR_IMG_SRC =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODgiIGhlaWdodD0iODgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgc3Ryb2tlPSIjMDAwIiBzdHJva2UtbGluZWpvaW49InJvdW5kIiBvcGFjaXR5PSIuMyIgZmlsbD0ibm9uZSIgc3Ryb2tlLXdpZHRoPSIzLjciPjxyZWN0IHg9IjE2IiB5PSIxNiIgd2lkdGg9IjU2IiBoZWlnaHQ9IjU2IiByeD0iNiIvPjxwYXRoIGQ9Im0xNiA1OCAxNi0xOCAzMiAzMiIvPjxjaXJjbGUgY3g9IjUzIiBjeT0iMzUiIHI9IjciLz48L3N2Zz4KCg==';

export function ImageWithFallback(props: ImgHTMLAttributes<HTMLImageElement>) {
  const [didError, setDidError] = useState(false);

  const handleError = () => {
    setDidError(true);
  };

  const { src, alt, style, className, loading, ...rest } = props;
  // Дефолтный loading="lazy" — вызывающий может перекрыть на "eager" для hero-картинок.
  const loadingAttr = loading ?? 'lazy';

  return didError ? (
    <div
      className={`inline-block bg-gray-100 text-center align-middle ${className ?? ''}`}
      style={style}
    >
      <div className="flex items-center justify-center w-full h-full">
        <img src={ERROR_IMG_SRC} alt="Failed to load" {...rest} data-original-url={src} />
      </div>
    </div>
  ) : (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      loading={loadingAttr}
      {...rest}
      onError={handleError}
    />
  );
}
